import { useState } from 'react'
import { Link } from 'react-router-dom'

import { FxAlert, FxButton, FxIcon, FxPanel } from '@/components/fx'
import type { TemplateField } from '@/types/domain'

import { ConfirmDialog, DialogShell } from './DialogShell'
import {
  contentLabel,
  editorPath,
  fieldTypeIcon,
  fieldTypeLabel,
  layoutName,
  type FieldRow,
  type LayoutEntry,
} from './templateTabModel'
import { editErrorMessage, type TemplateEdits } from './useTemplateEdits'

/**
 * "Felder": one row per export column, one column per layout.
 *
 * The matrix is the answer to the question a second layout creates — "does
 * the 2019 form still ask for the Geburtsort?" — and the cell is where that
 * gets fixed, because an empty cell is exactly a field the export will leave
 * blank for every document of that layout.
 */
export function FieldsPanel({
  templateId,
  rows,
  layouts,
  fields,
  canWrite,
  edits,
}: {
  templateId: string
  rows: FieldRow[]
  layouts: LayoutEntry[]
  /** The template's own field set, as the server last reported it — what a
   *  reorder is computed against. */
  fields: TemplateField[]
  canWrite: boolean
  edits: TemplateEdits
}) {
  const [deleting, setDeleting] = useState<FieldRow | null>(null)
  const { moveField, deleteField } = edits
  // Rows without a shared field id (Firestore, or a template older than
  // migration 00006) are grouped by label and have nothing the gateway could
  // reorder or delete — see `fieldRows`.
  const editable = canWrite && rows.every((row) => row.editable)

  return (
    <FxPanel
      title="Felder"
      meta={
        <span className="text-small text-text-muted">
          {rows.length === 1 ? '1 Feld' : `${rows.length} Felder`} · Reihenfolge = Spalten im Export
        </span>
      }
      className="p-5 xs:p-8"
    >
      {moveField.isError && (
        <p role="alert" className="mb-4 text-body text-danger-text">
          {editErrorMessage(moveField.error)}
        </p>
      )}

      {rows.length === 0 ? (
        <FxAlert tone="info" title="Noch keine Felder">
          <p>
            Felder entstehen im Editor: dort ziehen Sie Kästchen über die Stellen des Formulars, die
            gelesen werden sollen — oder lassen sie erkennen.
          </p>
          {layouts[0] && (
            <FxButton variant="tonal" asChild className="mt-4">
              <Link to={editorPath(templateId, layouts[0].page.page)}>Felder anlegen</Link>
            </FxButton>
          )}
        </FxAlert>
      ) : (
        <>
          {canWrite && !editable && (
            <p className="mb-4 text-small text-text-muted">
              Diese Vorlage führt ihre Felder noch über die Feldnamen. Umbenennen, Sortieren und
              Löschen geschehen deshalb im Editor.
            </p>
          )}

          {/* The only element on this screen allowed to scroll sideways: a
              form with five layouts has more columns than a phone has room
              for, and squeezing them would make every cell unreadable. */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-body">
              <caption className="sr-only">
                Felder der Vorlage und die Layouts, auf denen sie platziert sind
              </caption>
              <thead>
                <tr className="text-left">
                  <th scope="col" className="rounded-l-sm bg-fill px-3 py-2 text-small font-normal">
                    Feld
                  </th>
                  <th scope="col" className="bg-fill px-3 py-2 text-small font-normal">
                    Typ
                  </th>
                  <th scope="col" className="bg-fill px-3 py-2 text-small font-normal">
                    Inhalt
                  </th>
                  {layouts.map((layout) => (
                    <th
                      key={layout.page.page}
                      scope="col"
                      className="bg-fill px-3 py-2 text-center text-small font-normal"
                    >
                      {layoutName(layout.page)}
                    </th>
                  ))}
                  {editable && (
                    <th scope="col" className="bg-fill px-3 py-2 text-center text-small font-normal">
                      Reihenfolge
                    </th>
                  )}
                  {editable && (
                    <th scope="col" className="rounded-r-sm bg-fill px-3 py-2 text-center text-small font-normal">
                      Entfernen
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.id} className="border-b border-divider">
                    <th scope="row" className="px-3 py-2 text-left font-normal">
                      <span className="flex items-center gap-[10px]">
                        <FxIcon name={fieldTypeIcon(row.type)} size={20} className="text-primary" />
                        {row.name}
                      </span>
                    </th>
                    <td className="px-3 py-2">{fieldTypeLabel(row.type)}</td>
                    <td className="px-3 py-2">{contentLabel(row.type, row.textType)}</td>

                    {layouts.map((layout) => (
                      <td key={layout.page.page} className="px-3 py-2 text-center">
                        <PlacementCell
                          templateId={templateId}
                          row={row}
                          layout={layout}
                          canWrite={canWrite}
                        />
                      </td>
                    ))}

                    {editable && (
                      <td className="px-3 py-2">
                        <div className="flex justify-center gap-1">
                          <MoveButton
                            label={`„${row.name}“ eine Spalte nach vorn`}
                            icon="arrow_upward"
                            disabled={index === 0 || moveField.isPending}
                            onClick={() =>
                              moveField.mutate({ fieldId: row.id, direction: 'up', known: fields })
                            }
                          />
                          <MoveButton
                            label={`„${row.name}“ eine Spalte nach hinten`}
                            icon="arrow_downward"
                            disabled={index === rows.length - 1 || moveField.isPending}
                            onClick={() =>
                              moveField.mutate({ fieldId: row.id, direction: 'down', known: fields })
                            }
                          />
                        </div>
                      </td>
                    )}

                    {editable && (
                      <td className="px-3 py-2 text-center">
                        <button
                          type="button"
                          aria-label={`Feld „${row.name}“ entfernen`}
                          className="inline-flex rounded-sm p-1 text-text hover:text-danger-text"
                          onClick={() => setDeleting(row)}
                        >
                          <FxIcon name="delete" size={20} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-6 text-small text-text-muted">
            Ein Feld, das auf einer Fassung fehlt, bleibt im Export für Dokumente dieser Fassung
            leer.
          </p>
        </>
      )}

      <DeleteFieldDialog
        row={deleting}
        layouts={layouts}
        edits={edits}
        onClose={() => {
          deleteField.reset()
          setDeleting(null)
        }}
      />
    </FxPanel>
  )
}

/**
 * One cell of the matrix.
 *
 * The artboard draws a grey dash for "not on this layout". A dash is honest
 * but inert, and the thing a person wants at that moment is to put the field
 * there — so where they may edit, the empty cell is the link that opens the
 * editor on that layout with the field preselected. Where they may not, it
 * is the dash.
 */
function PlacementCell({
  templateId,
  row,
  layout,
  canWrite,
}: {
  templateId: string
  row: FieldRow
  layout: LayoutEntry
  canWrite: boolean
}) {
  const name = layoutName(layout.page)

  if (row.placedOn.includes(layout.page.page)) {
    return (
      <>
        <FxIcon name="check_circle" size={20} className="text-success-text" />
        <span className="sr-only">{`auf „${name}“ platziert`}</span>
      </>
    )
  }

  // A page that is still converting has no image to draw boxes on yet.
  if (canWrite && row.editable && layout.page.status === 'ready') {
    return (
      <Link
        to={editorPath(templateId, layout.page.page, row.id)}
        className="text-small text-primary-text"
      >
        Platzieren
        <span className="sr-only">{` – „${row.name}“ auf „${name}“`}</span>
      </Link>
    )
  }

  return (
    <>
      <FxIcon name="remove" size={20} className="text-text-disabled" />
      <span className="sr-only">{`nicht auf „${name}“`}</span>
    </>
  )
}

function MoveButton({
  label,
  icon,
  disabled,
  onClick,
}: {
  label: string
  icon: string
  disabled: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex rounded-sm p-1 text-text hover:text-primary-text disabled:text-text-disabled"
    >
      <FxIcon name={icon} size={20} />
    </button>
  )
}

/**
 * Deleting a field.
 *
 * A field that is still placed cannot be deleted — the gateway answers 409
 * `field_in_use` — and we already know from the matrix whether it is. So the
 * dialog either asks the question or explains why it cannot be asked, naming
 * the layouts; it never fires a request whose refusal is predictable.
 */
function DeleteFieldDialog({
  row,
  layouts,
  edits,
  onClose,
}: {
  row: FieldRow | null
  layouts: LayoutEntry[]
  edits: TemplateEdits
  onClose: () => void
}) {
  const { deleteField } = edits
  if (!row) return null

  const placedNames = layouts
    .filter((layout) => row.placedOn.includes(layout.page.page))
    .map((layout) => layoutName(layout.page))

  if (placedNames.length > 0) {
    return (
      <DialogShell
        open
        onClose={onClose}
        title="Dieses Feld ist noch platziert"
        description={
          <p>
            „{row.name}“ liegt noch auf {placedNames.join(', ')}. Entfernen Sie das Kästchen dort im
            Editor — danach lässt sich das Feld löschen.
          </p>
        }
      >
        <div className="flex justify-end">
          <FxButton variant="outlined" onClick={onClose}>
            Schließen
          </FxButton>
        </div>
      </DialogShell>
    )
  }

  return (
    <ConfirmDialog
      open
      onClose={onClose}
      title="Feld löschen?"
      description={
        <p>
          „{row.name}“ wird aus der Vorlage gelöscht und verschwindet als Spalte aus künftigen
          Exporten. Bereits gelesene Werte bleiben an den Dokumenten erhalten.
        </p>
      }
      confirmLabel="Löschen"
      pending={deleteField.isPending}
      error={deleteField.isError ? editErrorMessage(deleteField.error) : null}
      onConfirm={() => deleteField.mutate(row.id, { onSuccess: onClose })}
    />
  )
}
